import { type FC, useState } from "react";
import { Link } from "react-router-dom";
import { ButtonUI } from "../../../../components/Button";
import { PageTitle } from "../../../../components/PageTitle";
import { useOrganizationsRestApi } from "../../../../hooks/useOrganizationsRestApi";
import { CreateOrganizationModal } from "./components/CreateOrganizationModal";
import { OrganizationsTable } from "./components/OrganizationsTable";

export const OrganizationsPage: FC = () => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const { data, loading, error, refetch } = useOrganizationsRestApi();

  const organizations = data?.organizations || [];
  const totalCount = organizations.length;

  const handleCreateOrganization = () => {
    refetch();
    setIsCreateModalOpen(false);
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <PageTitle>組織管理</PageTitle>
          <p className="text-gray-600 mt-2">
            システム内の全ての組織を管理します
          </p>
        </div>
        <div className="flex space-x-3">
          <Link
            to="/admin"
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            ダッシュボードに戻る
          </Link>
          <ButtonUI
            onClick={() => setIsCreateModalOpen(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            新規組織作成
          </ButtonUI>
        </div>
      </div>

      {/* エラー表示 */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <div className="flex">
            <div className="text-red-400">⚠️</div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                エラーが発生しました
              </h3>
              <p className="mt-1 text-sm text-red-700">{error.message}</p>
            </div>
          </div>
        </div>
      )}

      {/* 統計情報 */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">組織統計</h3>
            <p className="text-sm text-gray-500">システム内の組織情報</p>
          </div>
          <div className="flex space-x-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">
                {totalCount}
              </div>
              <div className="text-sm text-gray-500">総組織数</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {organizations.filter(org => org.isActive).length}
              </div>
              <div className="text-sm text-gray-500">アクティブ</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">
                {organizations.reduce(
                  (sum, org) => sum + (org.employeeCount || 0),
                  0,
                )}
              </div>
              <div className="text-sm text-gray-500">総従業員数</div>
            </div>
          </div>
        </div>
      </div>

      {/* 組織一覧テーブル */}
      <div className="bg-white rounded-lg shadow">
        <OrganizationsTable
          organizations={organizations}
          isLoading={loading}
          onRefresh={() => refetch()}
        />
      </div>

      {/* 新規作成モーダル */}
      <CreateOrganizationModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={handleCreateOrganization}
      />
    </div>
  );
};
